import React, { useState } from 'react';
import { useQuery, useMutation, useReactiveVar } from '@apollo/client';
import { Page } from '../../layout';
import { Breadcrumb } from '../../shared/components/breadcrumb/Breadcrumb';
import PageInstructions from '../../shared/components/PageInstructions';
import ActionButtonGroup from '@shared/components/ActionButtonsGroup';
import SurveyResultsSection from './SurveyResultsSection';
import GET_SURVEY_RESULTS from '../queries/getSurveyResultsQuery';
import UPDATE_SURVEY_RESULTS from '../queries/updateSurveyResultsMutation';
import ApolloError from '@shared/components/ApolloError';
import { FormProvider, useForm } from 'react-hook-form';
import SurveyResultsPageValidator from '../validators/SurveyResultsPageValidator';
import { joiResolver } from '@hookform/resolvers/joi';
import SurveyResultsMapper from '../mappers/SurveyResultsMapper';
import { TOAST_MESSAGE_TYPES } from '@shared/constants/toastMessageTypes';
import { showToast } from '@shared/services/toastService';
import { userInfo } from '@apollo-internal/cache';
import PrivilegeNames from '../../../constants/privilegeNames';
import { DATA_DOWNLOAD_TYPES } from '@shared/constants/DataDownloadTypes';
import PdfCsvDownloadButton from '@shared/components/PdfCsvDownloadButton';
import { flatMap } from 'lodash';

const SAVE_SUCCESS_MESSAGE = 'Changes saved successfully';
const SURVEY_EDIT_FORM_ID = 'survey-edit-form';
const SAVE_BUTTON_TEXT = 'Save';
const MISSING_DATA_TEXT = 'Missing data in survey results';

const SurveyResultsPage = () => {
  const currentUserInfo = useReactiveVar(userInfo);
  const canUserEdit = currentUserInfo.Privileges.includes(PrivilegeNames.PostAppraisalSurvey_Edit);
  const canUserDownload = currentUserInfo.Privileges.includes(
    PrivilegeNames.PostAppraisalSurvey_Download
  );
  const [isEdit, setIsEdit] = useState(false);

  const { loading, error } = useQuery(GET_SURVEY_RESULTS, {
    onCompleted: data => {
      reset({ questions: SurveyResultsMapper.toSurveyResultsData(data.getSurveyResults) });
    }
  });

  const [updateSurveyResults, { error: updateError }] = useMutation(UPDATE_SURVEY_RESULTS);

  const form = useForm({
    resolver: joiResolver(SurveyResultsPageValidator.editSchema),
    defaultValues: { questions: [] },
    shouldFocusError: true
  });

  const { handleSubmit, getValues, formState, reset } = form;

  const handleFormSubmit = async () => {
    const formValues = getValues();
    const changedValues = getChangedSurveyValues(formState.dirtyFields, formValues);
    await updateSurveyResults({
      variables: { input: changedValues }
    });

    showToast(TOAST_MESSAGE_TYPES.SUCCESS, SAVE_SUCCESS_MESSAGE);
    reset(getValues());
    setIsEdit(false);
  };

  const handleFormInvalid = () => {
    showToast(TOAST_MESSAGE_TYPES.ERROR, MISSING_DATA_TEXT);
  };

  const handleCancelClick = () => {
    setIsEdit(false);
    reset(formState.defaultValues);
  };

  const handleEditClick = () => {
    setIsEdit(true);
  };

  if (error || updateError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle} className='survey-results'>
      <Breadcrumb pathItems={breadcrumbPathItems}>
        {canUserDownload && (
          <PdfCsvDownloadButton dataDownloadType={DATA_DOWNLOAD_TYPES.surveyResults} />
        )}
      </Breadcrumb>
      <PageInstructions title={pageTitle} description={description} />
      {canUserEdit && (
        <div className='survey-results__action-buttons'>
          <ActionButtonGroup
            isEdit={isEdit}
            handleEditClick={handleEditClick}
            handleCancelClick={handleCancelClick}
            handleSubmitClick={() => {}}
            submitForm={SURVEY_EDIT_FORM_ID}
            submitButtonType='submit'
            isSubmitting={loading}
            isSubmitDisabled={!formState.isDirty}
            submitButtonText={SAVE_BUTTON_TEXT}
          />
        </div>
      )}
      <FormProvider {...form}>
        <form id={SURVEY_EDIT_FORM_ID} onSubmit={handleSubmit(handleFormSubmit, handleFormInvalid)}>
          <SurveyResultsSection loading={loading} isEditMode={isEdit} />
        </form>
      </FormProvider>
    </Page>
  );
};

const getChangedSurveyValues = (dirtyFields, formValues) =>
  flatMap(dirtyFields?.questions, (dirtyQuestion, questionIndex) =>
    flatMap(dirtyQuestion?.SurveyResults, (dirtyResult, resultIndex) =>
      flatMap(dirtyResult?.SurveyResultValues, (dirtyValue, valueIndex) => {
        if (!dirtyValue) {
          return [];
        }

        const { Id, Value } =
          formValues.questions[questionIndex].SurveyResults[resultIndex].SurveyResultValues[
            valueIndex
          ];

        return [{ Id, Value }];
      })
    )
  );

const pageTitle = 'Post Appraisal Survey Results';
const breadcrumbPathItems = [
  {
    name: 'Survey',
    url: '/survey'
  }
];
const description = 'Post appraisal survey results';

export default SurveyResultsPage;
